/**
 * MonitorUsoDiarioModal.jsx — Componente de integração (produção)
 *
 * Integração no Veiculos.js:
 *   1. Importar: import MonitorUsoDiarioModal from '../components/MonitorUsoDiarioModal';
 *   2. Adicionar estado: const [showMonitor, setShowMonitor] = useState(false);
 *   3. Adicionar botão no header:
 *      <button onClick={() => setShowMonitor(true)} className="vec-btn vec-btn-outline">
 *        <FaUserClock /> Monitor Diário
 *        {/* Badge de discrepâncias (opcional, requer rota de polling) *\/}
 *      </button>
 *   4. Adicionar no final do JSX (antes do fechamento do <main>):
 *      {showMonitor && <MonitorUsoDiarioModal onClose={() => setShowMonitor(false)} />}
 *
 * Rotas backend necessárias (ver routesVehicles_monitor.js):
 *   GET  /api/vehicles/monitor-diario
 *   POST /api/vehicles/uso-diario/:usoId/ping
 *   (força logout já existe: POST /api/usuarios/logout-force/:id)
 *   (fechar uso já existe: PUT /api/vehicles/uso-diario/:usoId/fechar)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import './MonitorUsoDiarioModal.css';
import {
  FaTimes, FaSync, FaTruck, FaExclamationTriangle,
  FaBell, FaSignOutAlt, FaClock, FaUserClock,
  FaCheckCircle, FaCircle, FaPlus, FaCarSide,
  FaChevronDown,
} from 'react-icons/fa';

const AUTO_PING_STORAGE_KEY = 'frota_monitor_auto_ping_v1';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDuration = (totalSeconds) => {
  if (!totalSeconds && totalSeconds !== 0) return '—';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h === 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  if (h < 24) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  '#1B4F72', '#117A65', '#784212', '#6C3483',
  '#0E6655', '#1B6CA8', '#922B21', '#1A5276',
];
const getAvatarColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const statusOf = (row) => {
  const minAtivo = (row.minutos_ativo || 0);
  if (row.dias_aberto > 1) return { key: 'crit',    label: `${row.dias_aberto}d sem saída`, dot: '#EF4444', bg: '#FEF2F2', text: '#991B1B', border: '#EF4444' };
  if (row.dias_aberto === 1) return { key: 'warn',  label: 'ontem sem saída',               dot: '#F59E0B', bg: '#FFFBEB', text: '#92400E', border: '#F59E0B' };
  if (minAtivo > 600)        return { key: 'caution', label: '+10h ativo',                  dot: '#F59E0B', bg: '#FFFBEB', text: '#92400E', border: '#F59E0B' };
  return                            { key: 'ok',    label: 'em uso',                         dot: '#10B981', bg: '#ECFDF5', text: '#065F46', border: '#10B981' };
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function MonitorUsoDiarioModal({ onClose }) {
  const [dados, setDados] = useState({ hoje: [], discrepancias: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tab, setTab] = useState('hoje');
  const [clock, setClock] = useState(new Date());
  const [secs, setSecs] = useState(0); // seconds elapsed since modal opened, for live duration
  const [pings, setPings] = useState({});    // { usoId: 'loading'|'done'|'error' }
  const [logouts, setLogouts] = useState({}); // { usoId: 'confirm'|'loading'|'done' }
  const [schedOpen, setSchedOpen] = useState(false);
  const [pingLog, setPingLog] = useState([]);

  // Auto-ping config (persisted)
  const [autoPing, setAutoPing] = useState(false);
  const [forceLogoutOnNoResp, setForceLogoutOnNoResp] = useState(false);
  const [horarios, setHorarios] = useState(['18:00', '20:00']);
  const [newHr, setNewHr] = useState('');
  const [executedPings, setExecutedPings] = useState(new Set());

  const refreshIntervalRef = useRef(null);
  const clockIntervalRef = useRef(null);
  const pingIntervalRef = useRef(null);

  // ── Load / save persistent config ──────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(AUTO_PING_STORAGE_KEY);
      if (raw) {
        const cfg = JSON.parse(raw);
        setAutoPing(cfg.autoPing ?? false);
        setForceLogoutOnNoResp(cfg.forceLogout ?? false);
        setHorarios(cfg.horarios ?? ['18:00', '20:00']);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_PING_STORAGE_KEY, JSON.stringify({ autoPing, forceLogout: forceLogoutOnNoResp, horarios }));
    } catch { /* ignore */ }
  }, [autoPing, forceLogoutOnNoResp, horarios]);

  // ── Clock ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    clockIntervalRef.current = setInterval(() => {
      setClock(new Date());
      setSecs(s => s + 1);
    }, 1000);
    return () => clearInterval(clockIntervalRef.current);
  }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchDados = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const res = await api.get('/api/vehicles/monitor-diario');
      setDados(res.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('[MonitorUsoDiario]', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDados();
    refreshIntervalRef.current = setInterval(() => fetchDados(true), 30_000);
    return () => clearInterval(refreshIntervalRef.current);
  }, [fetchDados]);

  // ── Auto-ping scheduler ────────────────────────────────────────────────────
  useEffect(() => {
    if (!autoPing) { clearInterval(pingIntervalRef.current); return; }

    pingIntervalRef.current = setInterval(async () => {
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const key = `${now.toISOString().split('T')[0]}_${hhmm}`;

      if (!horarios.includes(hhmm) || executedPings.has(key)) return;
      setExecutedPings(prev => new Set([...prev, key]));

      const todos = [...dados.hoje, ...dados.discrepancias];
      for (const row of todos) {
        try {
          await api.post(`/api/vehicles/uso-diario/${row.uso_id}/ping`, {
            mensagem: `Verificação automática (${hhmm}): Você ainda está usando o veículo ${row.veiculo_placa}? Confirme ou registre a saída no app.`,
          });
          const entry = { id: Date.now() + row.uso_id, motorista: row.motorista_nome.split(' ')[0], placa: row.veiculo_placa, hora: hhmm };
          setPingLog(l => [entry, ...l].slice(0, 10));

          if (forceLogoutOnNoResp) {
            setTimeout(async () => {
              try {
                const check = await api.get('/api/vehicles/monitor-diario');
                const aindaAberto = [...check.data.hoje, ...check.data.discrepancias].find(r => r.uso_id === row.uso_id);
                if (aindaAberto) {
                  await api.put(`/api/vehicles/uso-diario/${row.uso_id}/fechar`);
                  if (row.usuario_id) await api.post(`/api/usuarios/logout-force/${row.usuario_id}`);
                }
              } catch { /* silent */ }
            }, 5 * 60 * 1000); // 5 min
          }
        } catch { /* silent */ }
      }
      await fetchDados(true);
    }, 60_000);

    return () => clearInterval(pingIntervalRef.current);
  }, [autoPing, horarios, forceLogoutOnNoResp, dados, executedPings, fetchDados]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handlePing = async (row) => {
    if (pings[row.uso_id]) return;
    setPings(p => ({ ...p, [row.uso_id]: 'loading' }));
    try {
      await api.post(`/api/vehicles/uso-diario/${row.uso_id}/ping`, {
        mensagem: `Olá, ${row.motorista_nome.split(' ')[0]}! Você ainda está usando o veículo ${row.veiculo_placa}? Por favor, confirme ou registre sua saída no app.`,
      });
      setPings(p => ({ ...p, [row.uso_id]: 'done' }));
      const entry = { id: Date.now(), motorista: row.motorista_nome.split(' ')[0], placa: row.veiculo_placa, hora: clock.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) };
      setPingLog(l => [entry, ...l].slice(0, 10));
    } catch {
      setPings(p => ({ ...p, [row.uso_id]: 'error' }));
    } finally {
      setTimeout(() => setPings(p => { const n = { ...p }; delete n[row.uso_id]; return n; }), 3500);
    }
  };

  const handlePingAll = () => {
    rows.forEach((r, i) => {
      if (!pings[r.uso_id]) setTimeout(() => handlePing(r), i * 250);
    });
  };

  const handleForcarSaida = async (row) => {
    const st = logouts[row.uso_id];
    if (st === 'confirm') {
      setLogouts(p => ({ ...p, [row.uso_id]: 'loading' }));
      try {
        await api.put(`/api/vehicles/uso-diario/${row.uso_id}/fechar`);
        if (row.usuario_id) await api.post(`/api/usuarios/logout-force/${row.usuario_id}`);
        setLogouts(p => ({ ...p, [row.uso_id]: 'done' }));
        setTimeout(() => fetchDados(true), 1000);
      } catch {
        setLogouts(p => { const n = { ...p }; delete n[row.uso_id]; return n; });
      }
    } else if (!st) {
      setLogouts(p => ({ ...p, [row.uso_id]: 'confirm' }));
      setTimeout(() => {
        setLogouts(p => { if (p[row.uso_id] === 'confirm') { const n = { ...p }; delete n[row.uso_id]; return n; } return p; });
      }, 5000);
    }
  };

  const addHorario = () => {
    if (!newHr || horarios.includes(newHr)) return;
    setHorarios(h => [...h, newHr].sort());
    setNewHr('');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const rows = tab === 'hoje' ? dados.hoje : dados.discrepancias;
  const hasDiscrep = dados.discrepancias.length > 0;

  return (
    <div className="mon-overlay" onClick={onClose}>
      <div className="mon-modal" onClick={e => e.stopPropagation()}>

        {/* HEADER */}
        <header className="mon-header">
          <div className="mon-header-left">
            <div className="mon-header-icon"><FaUserClock /></div>
            <div>
              <h2 className="mon-title">Monitor de Uso Diário</h2>
              <p className="mon-subtitle">
                {clock.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                <span className="mon-clock">
                  {clock.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              </p>
            </div>
          </div>
          <div className="mon-header-right">
            <div className="mon-pill mon-pill-green">
              <span className="mon-dot mon-dot-green mon-pulse-slow"></span>
              <strong>{dados.hoje.length}</strong> hoje
            </div>
            {hasDiscrep && (
              <div className="mon-pill mon-pill-red">
                <span className="mon-dot mon-dot-red mon-pulse-fast"></span>
                <strong>{dados.discrepancias.length}</strong> discrepância{dados.discrepancias.length !== 1 ? 's' : ''}
              </div>
            )}
            <button className="mon-icon-btn" onClick={() => fetchDados()} title="Atualizar">
              <FaSync className={isLoading ? 'mon-spin' : ''} />
            </button>
            <button className="mon-icon-btn mon-icon-btn-close" onClick={onClose} title="Fechar">
              <FaTimes />
            </button>
          </div>
        </header>

        {/* ALERT BANNER */}
        {hasDiscrep && (
          <div className="mon-alert-banner">
            <FaExclamationTriangle />
            <span>
              <strong>{dados.discrepancias.length} motorista{dados.discrepancias.length > 1 ? 's' : ''}</strong> com sessão aberta de dias anteriores.
            </span>
            <button className="mon-banner-cta" onClick={() => setTab('discrepancias')}>Ver agora →</button>
          </div>
        )}

        {/* TABS */}
        <div className="mon-tabs-bar">
          <div className="mon-tabs">
            <button className={`mon-tab ${tab === 'hoje' ? 'active' : ''}`} onClick={() => setTab('hoje')}>
              <FaTruck /> Em Uso Hoje
              <span className="mon-tab-badge">{dados.hoje.length}</span>
            </button>
            <button className={`mon-tab ${tab === 'discrepancias' ? 'active' : ''} ${hasDiscrep ? 'danger' : ''}`} onClick={() => setTab('discrepancias')}>
              <FaExclamationTriangle /> Discrepâncias
              <span className={`mon-tab-badge ${hasDiscrep ? 'danger' : ''}`}>{dados.discrepancias.length}</span>
            </button>
          </div>
          {rows.length > 0 && (
            <button className="mon-btn-ping-all" onClick={handlePingAll}>
              <FaBell /> Pingar todos ({rows.length})
            </button>
          )}
        </div>

        {/* CONTENT */}
        <div className="mon-content">
          {isLoading ? (
            <div className="mon-state-box">
              <div className="mon-spinner"></div>
              <p>Sincronizando uso dos veículos...</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="mon-state-box">
              <span className="mon-empty-icon">{tab === 'hoje' ? '🚗' : '✅'}</span>
              <p>{tab === 'hoje' ? 'Nenhum veículo em uso agora.' : 'Nenhuma discrepância. Tudo em ordem!'}</p>
            </div>
          ) : (
            <div className="mon-rows">
              {rows.map((row, i) => {
                const st = statusOf(row);
                const ps = pings[row.uso_id];
                const ls = logouts[row.uso_id];
                const durSecs = (row.minutos_ativo || 0) * 60 + secs;
                return (
                  <div key={row.uso_id} className={`mon-row mon-row-${st.key} ${ls === 'done' ? 'mon-row-exit' : ''}`} style={{ animationDelay: `${i * 55}ms` }}>
                    <div className="mon-avatar" style={{ background: getAvatarColor(row.motorista_nome) }}>
                      {getInitials(row.motorista_nome)}
                    </div>
                    <div className="mon-info">
                      <span className="mon-driver-name">{row.motorista_nome}</span>
                      <span className="mon-vehicle-label">
                        <FaCarSide />
                        {row.veiculo_descricao}
                        <code className="mon-placa">{row.veiculo_placa}</code>
                      </span>
                    </div>
                    <div className="mon-times">
                      <span className="mon-time-entry"><FaClock /> {row.entrada_em}</span>
                      <span className={`mon-time-dur ${(row.minutos_ativo || 0) > 600 ? 'warn' : ''}`}>
                        {fmtDuration(durSecs)}
                      </span>
                    </div>
                    <div className="mon-status-badge" style={{ background: st.bg, color: st.text }}>
                      <span className="mon-status-dot" style={{ background: st.dot, animation: st.key !== 'ok' ? 'mon-pulse 1.2s ease infinite' : 'none' }}></span>
                      {st.label}
                    </div>
                    <div className="mon-actions">
                      <button
                        className={`mon-btn-ping ${ps || ''}`}
                        onClick={() => handlePing(row)}
                        disabled={!!ps}
                        title="Enviar notificação: ainda está aí?"
                      >
                        {ps === 'loading' ? <FaSync className="mon-spin" /> : ps === 'done' ? <FaCheckCircle /> : ps === 'error' ? <FaTimes /> : <FaBell />}
                        <span>{ps === 'loading' ? 'Enviando...' : ps === 'done' ? 'Enviado!' : ps === 'error' ? 'Erro' : 'Ainda está aí?'}</span>
                      </button>
                      <button
                        className={`mon-btn-logout ${ls || ''}`}
                        onClick={() => handleForcarSaida(row)}
                        disabled={ls === 'loading' || ls === 'done'}
                        title="Fechar uso e forçar logout do app"
                      >
                        {ls === 'loading' ? <FaSync className="mon-spin" /> : ls === 'done' ? <FaCheckCircle /> : <FaSignOutAlt />}
                        <span>{ls === 'confirm' ? 'Confirmar?' : ls === 'loading' ? 'Encerrando...' : ls === 'done' ? 'Encerrado' : 'Forçar saída'}</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SCHEDULER FOOTER */}
        <footer className="mon-footer">
          <div className="mon-sched-toggle" onClick={() => setSchedOpen(s => !s)}>
            <div className="mon-sched-left">
              <span className={`mon-auto-dot ${autoPing ? 'on' : ''}`}></span>
              <FaBell />
              <span className="mon-sched-label">
                Auto-ping {autoPing ? '· Ativo' : '· Inativo'}
                {autoPing && horarios.length > 0 && (
                  <span className="mon-horarios-inline">{horarios.join(', ')}</span>
                )}
              </span>
            </div>
            <div className="mon-sched-right">
              {lastRefresh && <span className="mon-last-upd">atualizado {lastRefresh.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>}
              <FaChevronDown className={`mon-chevron ${schedOpen ? 'open' : ''}`} />
            </div>
          </div>

          {schedOpen && (
            <div className="mon-sched-body">
              <div className="mon-toggles-row">
                <div className="mon-toggle-item">
                  <span>Auto-ping automático</span>
                  <div className={`mon-toggle ${autoPing ? 'on' : ''}`} onClick={() => setAutoPing(a => !a)}>
                    <div className="mon-toggle-knob"></div>
                  </div>
                </div>
                <div className="mon-toggle-item">
                  <span>Forçar logout se sem resposta (5 min)</span>
                  <div className={`mon-toggle ${forceLogoutOnNoResp ? 'on danger' : ''}`} onClick={() => setForceLogoutOnNoResp(f => !f)}>
                    <div className="mon-toggle-knob"></div>
                  </div>
                </div>
              </div>

              {forceLogoutOnNoResp && (
                <div className="mon-force-warning">
                  <FaExclamationTriangle />
                  <span>Após o ping, o sistema aguardará 5 min. Sem resposta: encerra o registro de uso e força o logout no app do motorista.</span>
                </div>
              )}

              <p className="mon-section-label">Horários de verificação automática</p>
              <div className="mon-horarios-row">
                {horarios.map(h => (
                  <div key={h} className="mon-horario-chip">
                    <FaClock /> {h}
                    <button className="mon-chip-remove" onClick={() => setHorarios(arr => arr.filter(x => x !== h))}>
                      <FaTimes />
                    </button>
                  </div>
                ))}
                <div className="mon-horario-add">
                  <input type="time" value={newHr} onChange={e => setNewHr(e.target.value)} className="mon-time-input" />
                  <button className="mon-add-btn" onClick={addHorario} disabled={!newHr}>
                    <FaPlus />
                  </button>
                </div>
              </div>

              {pingLog.length > 0 && (
                <div className="mon-ping-log">
                  <p className="mon-section-label">Pings enviados nesta sessão</p>
                  {pingLog.map(entry => (
                    <div key={entry.id} className="mon-log-entry">
                      <span className="mon-log-time">{entry.hora}</span>
                      <span className="mon-log-badge">✓ enviado</span>
                      <span>{entry.motorista} · {entry.placa}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </footer>

      </div>
    </div>
  );
}